import React, {lazy, Suspense} from "react";
import Idle from 'react-idle'
import {BrowserRouter as Router, Redirect, Route, Switch} from "react-router-dom";
import BrowserRouter from "./BrowserRouter";
import UserSessionService from "./services/UserSessionService";
import AuthenticationService from "./services/AuthenticationService";
import {config} from "./config";
import GenericUtils from "./utils/GenericUtils";
import MainLoading from "./components/loading/MainLoading";

const TokenAuth = lazy(() => import("./views/auth/TokenAuth"));
const MainLayout = lazy(() => import("./layouts/MainLayout"));
const BlankLayout = lazy(() => import("./layouts/BlankLayout"));
const NotFoundView = lazy(() => import("./views/404/NotFound"));
const AuthenticatedRoute = lazy(() => import("./views/auth/AuthenticatedRoute"));
const LoginView = lazy(() => import("./views/auth/LoginView"));
const TermsOfUse = lazy(() => import("./legal/termsOfUse/TermsOfUse"));
const DataProtectionNotice = lazy(() => import("./legal/dataprotection/DataProtectionNotice"));
const HomepageView = lazy(() => import("./views/home/HomepageView"));
const ProgramView = lazy(() => import("./views/program/ProgramView"));
const EquipmentView = lazy(() => import("./views/equipment/EquipmentView"));
const ProcessView = lazy(() => import("./views/process/ProcessView"));
const ProgramFormContainer = lazy(() => import("./views/program/components/ProgramFormContainer"));
const EquipmentFormContainer = lazy(() => import("./views/equipment/components/EquipmentFormContainer"));
const ProcessFormContainer = lazy(() => import("./views/process/components/ProcessFormContainer"));
const AlertView = lazy(() => import("./views/alert/AlertListView"));

const Routes = () => (
    <Router>
        <Idle
            timeout={config.generic.sessionTimeout.idleTimeout}
            render={({idle}) => {
                const isTokenAuthRoute = window.location.pathname === '/token-auth';
                if(idle && GenericUtils.pathHasAuthenticationAndIsNotHomePage(window.location.pathname) && UserSessionService.getSessionIsExpired()){
                    AuthenticationService.logout(true, () => {}, () => {
                        AuthenticationService.logoutWithoutRequest();
                    });
                    return null;
                } else {
                    if (!idle && !UserSessionService.getSessionIsExpired()
                        && GenericUtils.pathHasAuthenticationAndIsNotHomePage(window.location.pathname)
                        && !isTokenAuthRoute) {
                        AuthenticationService.extendSessionToken();
                    }
                    return (
                        <BrowserRouter>
                            <Switch>
                                <Route exact path="/" render={appProps => renderHomeRoute(appProps)}/>
                                <Route path="/token-auth" render={appProps => renderTokenAuthRoute(appProps)} />

                                <Route path="/dashboard" render={appProps => renderHomeRoute(appProps)}/>

                                <Route path="/program/:programId/details" render={appProps => renderProgramFormRoute(appProps)}/>
                                <Route path="/program/create" render={appProps => renderProgramFormRoute(appProps)}/>
                                <Route path="/program" render={appProps => renderProgramRoute(appProps)}/>

                                <Route path="/equipment/:equipmentId/details" render={appProps => renderEquipmentFormRoute(appProps)}/>
                                <Route path="/equipment/create" render={appProps => renderEquipmentFormRoute(appProps)}/>
                                <Route path="/equipment" render={appProps => renderEquipmentRoute(appProps)}/>

                                <Route path="/process/:processId/details" render={appProps => renderProcessFormRoute(appProps)}/>
                                <Route path="/process/create" render={appProps => renderProcessFormRoute(appProps)}/>
                                <Route path="/process" render={appProps => renderProcessRoute(appProps)}/>

                                <Route path="/alerts" render={appProps => renderAlertRoute(appProps)}/>

                                <Route path="/terms-of-use" render={appProps => renderTermsOfUse(appProps)}/>
                                <Route path="/data-protection-notice" render={appProps => renderDataProtectionNotice(appProps)}/>

                                <Route path="/login" render={appProps => renderLoginRoute(appProps)}/>

                                <Route path="/not_found" render={() => renderDefaultRoute()}/>
                                <Route path="*" render={() => renderDefaultRoute()}/>
                            </Switch>
                        </BrowserRouter>
                    );
                }
            }} />
    </Router>
);

function renderTokenAuthRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <TokenAuth {...props} />
    </Suspense>;
}

function renderHomeRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"dashboard"} shouldAddFullScreenCallback={true} {...props}>
                <HomepageView {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderProgramFormRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"program"} {...props}>
                <ProgramFormContainer {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderProgramRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"program"} {...props}>
                <ProgramView {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderEquipmentFormRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"equipment"} {...props}>
                <EquipmentFormContainer {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderEquipmentRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"equipment"} {...props}>
                <EquipmentView {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderProcessFormRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"process"} {...props}>
                <ProcessFormContainer {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderProcessRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"process"} {...props}>
                <ProcessView {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderAlertRoute(props) {
    return <Suspense fallback={<MainLoading/>}>
        <AuthenticatedRoute {...props}>
            <MainLayout pageName={"alert"} {...props}>
                <AlertView {...props}/>
            </MainLayout>
        </AuthenticatedRoute>
    </Suspense>;
}

function renderTermsOfUse(props) {
    return <Suspense fallback={<MainLoading/>}>
        <TermsOfUse {...props}/>
    </Suspense>;
}

function renderDataProtectionNotice(props) {
    return <Suspense fallback={<MainLoading/>}>
        <DataProtectionNotice {...props}/>
    </Suspense>;
}

function renderLoginRoute(props) {
    return UserSessionService.isAuthenticated()? <Redirect to={{pathname: '/dashboard'}}/> : (
        <Suspense fallback={<MainLoading/>}>
            <BlankLayout {...props}>
                <LoginView {...props}/>
            </BlankLayout>
        </Suspense>
    )
}

function renderDefaultRoute() {
    return <BlankLayout><NotFoundView/></BlankLayout>;
}

export default Routes;